import type { ViewProps } from "gemi/client";
import { Loader2Icon } from "lucide-react";
import { useEffect, useRef } from "react";
import { buildBeginCheckoutParams } from "@/app/utils/subscriptionAnalytics";
import { trackEvent } from "@/app/views/utils/analytics";

export default function StripeRedirect(
  props: ViewProps<"/checkout/:priceCode">,
) {
  // This page is a single checkout start, so begin_checkout fires at most once per load (no cross-load
  // dedup needed). `firedRef` guards a second dispatch when the effect re-runs on a prop-identity
  // change; the first run's callback/timer owns the redirect, so a re-run must not fire a second one.
  const firedRef = useRef(false);
  const mountedRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Clear the fallback timer only on a true unmount, never on an effect re-run: clearing it on re-run
  // would drop the begin_checkout hit that gtag.js may still be flushing from `dataLayer`.
  useEffect(() => {
    // Reset on (re)mount so a StrictMode dev remount, which runs the cleanup below, does not leave
    // `mountedRef` stuck false.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    const { destination, checkout } = props;
    if (!destination) return;
    // Already dispatched this load: the first run's callback + timer own the redirect. A re-run must
    // not navigate again, which could cancel the in-flight begin_checkout hit.
    if (firedRef.current) return;

    // Only navigate while still mounted: a gtag callback landing after the buyer went back within the
    // timeout window must not push them to Stripe.
    const go = () => {
      if (mountedRef.current) window.location.href = destination;
    };

    const gtag = (window as unknown as { gtag?: unknown }).gtag;
    // No gtag (a non-prod host silences GA) or no analytics payload: redirect straight away. Still
    // mark as fired so a re-run takes the early return above rather than navigating a second time.
    if (!checkout || typeof gtag !== "function") {
      firedRef.current = true;
      go();
      return;
    }
    firedRef.current = true;

    // Fire begin_checkout, then redirect once the hit is sent (or a short timeout elapses) so the
    // full-page navigation to Stripe does not cancel it. Same settle/fallback idiom as the callback,
    // with a tighter 500ms budget since a redirect follows.
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      go();
    };
    trackEvent(
      { event: "begin_checkout", params: buildBeginCheckoutParams(checkout) },
      { event_callback: settle, event_timeout: 500 },
    );
    timerRef.current = setTimeout(settle, 500);
  }, [props.destination, props.checkout]);

  // Show a spinner rather than a blank page during the brief begin_checkout wait before the redirect
  // to Stripe, matching the sibling callback view.
  return (
    <div className="flex items-center justify-center min-h-dvh">
      <Loader2Icon className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}
